import { useState, useCallback, useEffect, useContext, createContext, type ReactNode } from 'react'

export interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'info'
}

let toastId = 0
let currentToasts: Toast[] = []
let listeners: Set<() => void> = new Set()

function notifyListeners() {
  listeners.forEach((listener) => listener())
}

const ToastContext = createContext<ToastContextValue | null>(null)

interface ToastContextValue {
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    const listener = () => {
      forceUpdate((n) => n + 1)
      return undefined
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
      return undefined
    }
  }, [])

  const addToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    const id = `toast-${++toastId}`
    const newToast: Toast = { id, message, type }
    currentToasts = [...currentToasts, newToast]
    notifyListeners()

    setTimeout(() => {
      currentToasts = currentToasts.filter(t => t.id !== id)
      notifyListeners()
    }, 3000)
  }, [])

  const value: ToastContextValue = {
    success: (message: string) => addToast(message, 'success'),
    error: (message: string) => addToast(message, 'error'),
    info: (message: string) => addToast(message, 'info'),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={currentToasts} />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return ctx
}

interface ToastContainerProps {
  toasts: Toast[]
}

function ToastContainer({ toasts }: ToastContainerProps) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`max-wxs rounded-lg border px-4 py-3 text-sm font-medium shadow-lg ${
            toast.type === 'success'
              ? 'border-[var(--color-success)] bg-[var(--color-success-light)] text-[var(--color-success)]'
              : toast.type === 'error'
              ? 'border-[var(--color-danger)] bg-[var(--color-danger-light)] text-[var(--color-danger)]'
              : 'border-[var(--color-brand-500)] bg-[var(--color-brand-100)] text-[var(--color-brand-500)]'
          }}`}
        >
          {toast.message}
        </div>
      ))}
    </div>
  )
}